import { useMemo } from "react";
import type { ProductCacheItem } from "../../../types/session.ts";
import type { AdvancedFilterProps } from "./types.ts";
import { FreeTextIdentityFilter } from "./freeTextIdentityFilter.tsx";
import type { FreeTextIdentityConfig, GroupFieldDef } from "./freeTextIdentityFilter.tsx";
import { formatOperatorId, useOperatorNameById } from "./identityVariants/shared.tsx";

/** `Other` may arrive as a single string OR a string array (some endpoints
 *  emit an array when a product has multiple external refs). Coerce to a
 *  flat comma-separated string for template + matching purposes. */
function otherText(o: unknown): string {
  if (!o) return "";
  if (Array.isArray(o)) return o.filter((x) => typeof x === "string" && x).join(", ");
  return typeof o === "string" ? o : String(o);
}

function templateFn(item: ProductCacheItem): string {
  // `Other` holds the per-module external-reference style field: ArticleNumber
  // on Access/AIM, Brand on NeoCaddie (see WebCache.Product.vb Commands*). Show
  // when present so operators can locate by SKU / brand alongside Name.
  const base = `(${item.ProductID}) ${item.Name ?? ""}`;
  const o = otherText(item.Other);
  return o ? `${base} [${o}]` : base;
}

function matchFn(item: ProductCacheItem, text: string): boolean {
  const lc = text.toLowerCase();
  const o = otherText(item.Other).toLowerCase();
  return (
    item.ProductID.toString().indexOf(lc) === 0 ||
    (!!item.Name && item.Name.toLowerCase().indexOf(lc) >= 0) ||
    (!!o && o.indexOf(lc) >= 0)
  );
}

export function ProductFilter({ info, isParameter, value, onChange, onSearch, cache }: AdvancedFilterProps) {
  const items = (cache.CommonData?.Product ?? []) as ProductCacheItem[];

  const operatorNameById = useOperatorNameById(cache.CommonData?.Common);

  /**
   * Product groups. Core fields on ProductCacheItem are minimal, so we derive:
   *   • Alphabetic — first letter of Name (A–Z navigation for big catalogues).
   *   • Status     — Active / Deleted.
   *   • Owner/Operator ID — from a runtime OperatorID field (may be null).
   *     Hidden automatically when no product carries a value.
   */
  const groupFields = useMemo<GroupFieldDef[]>(() => [
    {
      label: "Alphabetic",
      field: "__alpha",
      getValue: (item) => {
        const name = (item as ProductCacheItem).Name ?? "";
        const c = name.trim().charAt(0).toUpperCase();
        return /[A-Z]/.test(c) ? c : c ? "#" : "";
      },
    },
    {
      label: "Status",
      field: "__status",
      getValue: (item) => ((item as ProductCacheItem).Deleted ? "Deleted" : "Active"),
    },
    {
      label: "Owner/Operator ID",
      field: "OperatorID",
      getValue: (item) => formatOperatorId((item as Record<string, unknown>).OperatorID, operatorNameById),
    },
  ], [operatorNameById]);

  const config = useMemo<FreeTextIdentityConfig<ProductCacheItem>>(
    () => ({
      idField: "ProductID",
      nameField: "Name",
      items,
      matchFn,
      templateFn,
      groupFields,
      getIdFromItem: (item) => item.ProductID,
    }),
    [items, groupFields],
  );

  return (
    <FreeTextIdentityFilter
      config={config}
      columnName={info.ColumnName}
      isParameter={isParameter}
      value={value}
      onChange={onChange}
      onSearch={onSearch}
    />
  );
}
